import { useMoney } from '../hooks/useMoney';
/**
 * SERIAL NUMBER DETAIL PAGE
 *
 * View the complete history of a serial number through its lifecycle.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Spinner,
  Divider,
  Link,
  Icon,
} from '@shopify/polaris';
import {
  ClockIcon,
  PackageIcon,
  DeliveryIcon,
} from '@shopify/polaris-icons';
import { useApi } from '../hooks/useApi';

interface SerialHistory {
  id: number;
  event_type: string;
  order_id: number | null;
  order_number: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
}

interface SerialDetail {
  id: number;
  serial_number: string;
  product_id: number;
  product_sku: string;
  product_title: string;
  batch_number: string | null;
  location_id: number | null;
  location_name: string | null;
  status: string;
  sold_to_order_id: number | null;
  order_number: string | null;
  cost_price: number | null;
  received_at: string;
  sold_at: string | null;
  history: SerialHistory[];
}

export default function SerialNumberDetail() {
  const { serial } = useParams();
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [detail, setDetail] = useState<SerialDetail | null>(null);

  const loadDetail = useCallback(async () => {
    if (!serial) return;

    const data = await fetchApi<{ serial: SerialDetail }>(
      `/inventory/serials/${encodeURIComponent(serial)}`
    );
    if (data?.serial) {
      setDetail(data.serial);
    }
  }, [fetchApi, serial]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge tone="success">Available</Badge>;
      case 'allocated':
        return <Badge tone="info">Allocated</Badge>;
      case 'sold':
        return <Badge>Sold</Badge>;
      case 'returned':
        return <Badge tone="warning">Returned</Badge>;
      case 'written_off':
        return <Badge tone="critical">Written Off</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'received':
        return PackageIcon;
      case 'allocated':
      case 'sold':
        return DeliveryIcon;
      default:
        return ClockIcon;
    }
  };

  const getEventLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      received: 'Received',
      allocated: 'Allocated to Order',
      sold: 'Sold',
      returned: 'Returned',
      written_off: 'Written Off',
    };
    return labels[eventType] || eventType;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = useMoney();

  if (loading && !detail) {
    return (
      <Page title="Serial Number">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading serial number...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error || !detail) {
    return (
      <Page title="Serial Number">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              {error || 'Serial number not found'}
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={detail.serial_number}
      subtitle="Serial Number Details"
      backAction={{ content: 'Serial Numbers', onAction: () => navigate('/serial-numbers') }}
      titleMetadata={getStatusBadge(detail.status)}
    >
      <Layout>
        {/* Product Info */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Product</Text>
              <BlockStack gap="200">
                <Link
                  onClick={() => navigate(`/products/${detail.product_id}`)}
                  monochrome
                  removeUnderline
                >
                  <Text as="p" fontWeight="semibold">{detail.product_title}</Text>
                </Link>
                <Text as="p" tone="subdued">SKU: {detail.product_sku || '-'}</Text>
                {detail.batch_number && (
                  <Text as="p" tone="subdued">Batch: {detail.batch_number}</Text>
                )}
              </BlockStack>

              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Location</Text>
                  <Text as="span">{detail.location_name || 'Not assigned'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Cost Price</Text>
                  <Text as="span">{formatCurrency(detail.cost_price)}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" tone="subdued">Received</Text>
                  <Text as="span">{formatDate(detail.received_at)}</Text>
                </InlineStack>
                {detail.sold_at && (
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Sold</Text>
                    <Text as="span">{formatDate(detail.sold_at)}</Text>
                  </InlineStack>
                )}
              </BlockStack>

              {detail.order_number && (
                <>
                  <Divider />
                  <BlockStack gap="200">
                    <Text as="p" fontWeight="semibold">Sold To Order</Text>
                    <Link
                      onClick={() => navigate(`/orders/${detail.sold_to_order_id}`)}
                    >
                      {detail.order_number}
                    </Link>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* History Timeline */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">History</Text>

              {detail.history && detail.history.length > 0 ? (
                <div style={{ position: 'relative' }}>
                  {detail.history.map((event, index) => {
                    const IconComponent = getEventIcon(event.event_type);
                    const isLast = index === detail.history.length - 1;

                    return (
                      <div
                        key={event.id}
                        style={{
                          display: 'flex',
                          position: 'relative',
                          paddingBottom: isLast ? 0 : '20px',
                        }}
                      >
                        {/* Timeline line */}
                        {!isLast && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '15px',
                              top: '32px',
                              bottom: 0,
                              width: '2px',
                              background: '#E5E7EB',
                            }}
                          />
                        )}

                        {/* Icon circle */}
                        <div
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: '#4a9b8c',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            zIndex: 1,
                          }}
                        >
                          <div style={{ color: 'white' }}>
                            <Icon source={IconComponent} />
                          </div>
                        </div>

                        {/* Content */}
                        <div style={{ marginLeft: '12px', flex: 1 }}>
                          <Text as="p" fontWeight="semibold">
                            {getEventLabel(event.event_type)}
                          </Text>
                          <Text as="p" tone="subdued" variant="bodySm">
                            {formatDate(event.created_at)}
                            {event.created_by && ` by ${event.created_by}`}
                          </Text>
                          {event.order_number && (
                            <InlineStack gap="200" blockAlign="center">
                              <Text as="span" tone="subdued" variant="bodySm">
                                Order:
                              </Text>
                              <Link
                                onClick={() => navigate(`/orders/${event.order_id}`)}
                              >
                                {event.order_number}
                              </Link>
                            </InlineStack>
                          )}
                          {event.notes && (
                            <div
                              style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                background: '#F9FAFB',
                                borderRadius: '4px',
                              }}
                            >
                              <Text as="p" variant="bodySm">{event.notes}</Text>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Text as="p" tone="subdued">No history recorded yet.</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
